import { useState, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { InfoTooltip } from "../shared/InfoTooltip"
import { ContactListDialog } from "../shared/ContactListDialog"
import { ExportDialog, ExportButton, type ExportColumn } from "../shared/ExportDialog"
import { CHART_CONFIG } from "../../utils/constants"
import { Clock, AlertTriangle, Phone, User, ExternalLink, CheckCircle2, MessageCircle } from "lucide-react"
import type { FlagBreakdown, FlagBreakdownContact } from "../../hooks/useAnalytics"

interface EscalationRate {
  name: string
  count: number
  percentage: number
}

interface SlaBucket {
  label: string
  count: number
  color: string
  contacts?: FlagBreakdownContact[]
}

interface SlaMetrics {
  totalContacts: number
  totalWithSla: number
  percentWithSla: number
  breached: number
  percentBreached: number
  buckets: SlaBucket[]
}

interface EscalationsTabProps {
  flagBreakdown: FlagBreakdown[]
  resolvedTickets?: number
  resolvedContacts?: FlagBreakdownContact[]
  activeTickets?: number
  activeContacts?: FlagBreakdownContact[]
  totalEscalatedContacts?: FlagBreakdownContact[]
  convosWithResolvedFlagContacts?: FlagBreakdownContact[]
  needsReplyCount?: number
  needsReplyContacts?: FlagBreakdownContact[]
  escalationRates?: EscalationRate[]
  slaMetrics?: SlaMetrics
}

interface ContactDialog {
  title: string
  contacts: FlagBreakdownContact[]
  showStatusBadge?: boolean
}

export function EscalationsTab({
  flagBreakdown,
  resolvedTickets = 0, resolvedContacts = [],
  activeTickets = 0, activeContacts = [],
  totalEscalatedContacts = [],
  convosWithResolvedFlagContacts = [],
  needsReplyCount = 0, needsReplyContacts = [],
  escalationRates = [],
  slaMetrics,
}: EscalationsTabProps) {
  const [selectedBreakdown, setSelectedBreakdown] = useState<FlagBreakdown | null>(null)
  const [dialogFilter, setDialogFilter] = useState<"all" | "active" | "resolved">("all")
  const [contactDialog, setContactDialog] = useState<ContactDialog | null>(null)
  const [exportOpen, setExportOpen] = useState(false)

  const { escalationExportColumns, escalationExportData } = useMemo(() => {
    const columns: ExportColumn[] = [
      { key: "contactId", label: "Contact ID" },
      { key: "phoneNumber", label: "Phone Number" },
      { key: "name", label: "Name" },
      { key: "flagName", label: "Flag / Category / Severity" },
      { key: "status", label: "Status (active/resolved)" },
    ]
    const data: Record<string, any>[] = []
    for (const flag of flagBreakdown) {
      for (const c of flag.contacts || []) {
        data.push({ contactId: c.contactId, phoneNumber: c.phoneNumber || "", name: c.name || "", flagName: flag.name, status: c.status })
      }
    }
    return { escalationExportColumns: columns, escalationExportData: data }
  }, [flagBreakdown])

  const openDialog = (title: string, contacts: FlagBreakdownContact[], showStatusBadge = false) => {
    if (contacts.length > 0) setContactDialog({ title, contacts, showStatusBadge })
  }

  const flagData = flagBreakdown.filter(f => f.kind === "flag")
  const categoryData = flagBreakdown.filter(f => f.kind === "category")
  const severityData = flagBreakdown.filter(f => f.kind === "severity")
  const slaBuckets = slaMetrics?.buckets.filter(b => b.count > 0) || []

  const handleBarClick = (name: string) => {
    const entry = flagBreakdown.find(f => f.name === name)
    if (entry?.contacts && entry.contacts.length > 0) { setSelectedBreakdown(entry); setDialogFilter("all") }
  }

  const dialogContacts = selectedBreakdown?.contacts?.filter(c =>
    dialogFilter === "all" || c.status === dialogFilter
  ) || []

  const totalEscalated = activeTickets + resolvedTickets

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <ExportButton onClick={() => setExportOpen(true)} label="Export Escalations" />
      </div>

      <ExportDialog
        open={exportOpen} onOpenChange={setExportOpen}
        title="Export Escalation Data"
        description={`Export ${escalationExportData.length} escalation records as CSV`}
        columns={escalationExportColumns} data={escalationExportData}
        filename="telerivet_escalations"
      />

      {/* Escalation Summary Cards */}
      {(totalEscalated > 0 || needsReplyCount > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { label: "Total Escalated", value: totalEscalated, contacts: totalEscalatedContacts, showStatus: true, sub: "Contacts with flags" },
            { label: "Active", value: activeTickets, contacts: activeContacts, icon: <AlertTriangle className="h-3 w-3 text-amber-500" />, valueClass: "text-amber-600", sub: "Unresolved flags" },
            { label: "Resolved", value: resolvedTickets, contacts: resolvedContacts, icon: <CheckCircle2 className="h-3 w-3 text-green-500" />, valueClass: "text-green-600", sub: totalEscalated > 0 ? `${((resolvedTickets / totalEscalated) * 100).toFixed(0)}% of escalated` : "—" },
            { label: "Has Resolved Flag", value: convosWithResolvedFlagContacts.length, contacts: convosWithResolvedFlagContacts, icon: <CheckCircle2 className="h-3 w-3 text-emerald-400" />, valueClass: "text-emerald-600", showStatus: true, sub: "Any flag ever resolved" },
            { label: "Needs Reply", value: needsReplyCount, contacts: needsReplyContacts, icon: <MessageCircle className="h-3 w-3 text-blue-500" />, valueClass: "text-blue-600", sub: "Last message incoming, not dismissed" },
          ].map(({ label, value, contacts, icon, valueClass, showStatus, sub }) => (
            <Card key={label} className={contacts.length > 0 ? "cursor-pointer hover:bg-muted/30 transition-colors" : ""} onClick={() => openDialog(label, contacts, showStatus)}>
              <CardContent className="pt-4 pb-3">
                <div className="text-[10px] text-muted-foreground uppercase flex items-center gap-1">{icon}{label}</div>
                <div className={`text-2xl font-bold mt-1 ${valueClass || ""}`}>{value}</div>
                <div className="text-[10px] text-muted-foreground">{sub}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* SLA Overview */}
      {slaMetrics && slaMetrics.totalWithSla > 0 && (
        <div className="space-y-4">
          {(() => {
            const allSlaContacts = slaMetrics.buckets.flatMap(b => b.contacts || [])
            const breachedContacts = slaMetrics.buckets.find(b => b.label === "Breached")?.contacts || []
            const dueWithin4hContacts = [
              ...(slaMetrics.buckets.find(b => b.label === "< 1 hour")?.contacts || []),
              ...(slaMetrics.buckets.find(b => b.label === "1-4 hours")?.contacts || []),
            ]
            const onTrackContacts = [
              ...(slaMetrics.buckets.find(b => b.label === "1-3 days")?.contacts || []),
              ...(slaMetrics.buckets.find(b => b.label === "> 3 days")?.contacts || []),
            ]
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Conversations with SLA", value: slaMetrics.totalWithSla, contacts: allSlaContacts, sub: `${slaMetrics.percentWithSla.toFixed(1)}% of ${slaMetrics.totalContacts}` },
                  { label: "SLA Breached", value: slaMetrics.breached, contacts: breachedContacts, icon: <AlertTriangle className="h-3 w-3 text-red-500" />, valueClass: "text-red-600", sub: `${slaMetrics.percentBreached.toFixed(1)}% of SLA convos` },
                  { label: "Due within 4h", value: dueWithin4hContacts.length, contacts: dueWithin4hContacts, valueClass: "text-amber-600", sub: "Needs attention soon" },
                  { label: "On track", value: onTrackContacts.length, contacts: onTrackContacts, valueClass: "text-green-600", sub: "> 24h remaining" },
                ].map(({ label, value, contacts, icon, valueClass, sub }) => (
                  <Card key={label} className={contacts.length > 0 ? "cursor-pointer hover:bg-muted/30 transition-colors" : ""} onClick={() => openDialog(label, contacts)}>
                    <CardContent className="pt-4 pb-3">
                      <div className="text-[10px] text-muted-foreground uppercase flex items-center gap-1">{icon}{label}</div>
                      <div className={`text-2xl font-bold mt-1 ${valueClass || ""}`}>{value}</div>
                      <div className="text-[10px] text-muted-foreground">{sub}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          })()}

          {slaBuckets.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  <Clock className="h-4 w-4 inline mr-1.5" />
                  SLA Status Distribution
                  <InfoTooltip text="How many conversations fall into each SLA time bucket. Breached means the SLA deadline has passed." />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={CHART_CONFIG} className="w-full" style={{ height: Math.max(120, slaBuckets.length * 35 + 40) }}>
                  <BarChart data={slaBuckets} layout="vertical" barCategoryGap={6} style={{ cursor: "pointer" }}
                    onClick={e => { const label = e?.activeLabel; if (label) { const b = slaBuckets.find(x => x.label === label); if (b?.contacts?.length) openDialog(`SLA: ${label}`, b.contacts) } }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                    <XAxis type="number" className="text-xs" />
                    <YAxis dataKey="label" type="category" width={90} className="text-xs" tick={{ fontSize: 11 }} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="count" name="Conversations" barSize={16}>
                      {slaBuckets.map((entry, i) => <Cell key={`sla-${i}`} fill={entry.color} />)}
                    </Bar>
                  </BarChart>
                </ChartContainer>
                <p className="text-[10px] text-muted-foreground mt-1">Click a bar to view contacts</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Flags Chart */}
      {flagData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Flags <InfoTooltip text="Active vs resolved counts per contact. Click a bar to see contacts." /></CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={CHART_CONFIG} className="w-full" style={{ height: Math.max(150, flagData.length * 45 + 40) }}>
              {/* @ts-ignore */}
              <BarChart data={flagData} layout="vertical" barCategoryGap={10} onClick={e => { if (e?.activeLabel) handleBarClick(e.activeLabel) }} style={{ cursor: "pointer" }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                <XAxis type="number" className="text-xs" />
                <YAxis dataKey="name" type="category" width={130} className="text-xs" tick={{ fontSize: 11 }} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="active" stackId="a" name="Active" barSize={18}>{flagData.map((e, i) => <Cell key={`fa-${i}`} fill={e.color} />)}</Bar>
                <Bar dataKey="resolved" stackId="a" fill="#10b981" name="Resolved" barSize={18} />
              </BarChart>
            </ChartContainer>
            <p className="text-[10px] text-muted-foreground mt-1">Click a bar to view contacts</p>
          </CardContent>
        </Card>
      )}

      {/* Category + Severity side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {categoryData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Risk Categories <InfoTooltip text="Breakdown by risk category per contact. Click a bar to see contacts." /></CardTitle></CardHeader>
            <CardContent>
              <ChartContainer config={CHART_CONFIG} className="w-full" style={{ height: Math.max(150, categoryData.length * 40 + 40) }}>
                {/* @ts-ignore */}
                <BarChart data={categoryData} layout="vertical" barCategoryGap={8} onClick={e => { if (e?.activeLabel) handleBarClick(e.activeLabel) }} style={{ cursor: "pointer" }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis type="number" className="text-xs" />
                  <YAxis dataKey="name" type="category" width={160} className="text-xs" tick={{ fontSize: 10 }} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="active" stackId="a" name="Active" barSize={14}>{categoryData.map((e, i) => <Cell key={`ca-${i}`} fill={e.color} />)}</Bar>
                  <Bar dataKey="resolved" stackId="a" fill="#10b981" name="Resolved" barSize={14} />
                </BarChart>
              </ChartContainer>
              <p className="text-[10px] text-muted-foreground mt-1">Click a bar to see contacts</p>
            </CardContent>
          </Card>
        )}
        {severityData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Severity Scores <InfoTooltip text="Distribution of flagged conversations by contact severity score. Click a bar to see contacts." /></CardTitle></CardHeader>
            <CardContent>
              <ChartContainer config={CHART_CONFIG} className="w-full" style={{ height: Math.max(150, severityData.length * 40 + 40) }}>
                {/* @ts-ignore */}
                <BarChart data={severityData} layout="vertical" barCategoryGap={8} onClick={e => { if (e?.activeLabel) handleBarClick(e.activeLabel) }} style={{ cursor: "pointer" }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis type="number" className="text-xs" />
                  <YAxis dataKey="name" type="category" width={120} className="text-xs" tick={{ fontSize: 11 }} tickFormatter={(v: string) => v.replace("Severity ", "")} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="active" stackId="a" name="Active" barSize={14}>{severityData.map((e, i) => <Cell key={`la-${i}`} fill={e.color} />)}</Bar>
                  <Bar dataKey="resolved" stackId="a" fill="#10b981" name="Resolved" barSize={14} />
                </BarChart>
              </ChartContainer>
              <p className="text-[10px] text-muted-foreground mt-1">Click a bar to see contacts</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Resolution Rate + Escalation Rates */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {flagData.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Resolution Rate <InfoTooltip text="Percentage of flagged contacts that have been fully resolved" /></CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {flagData.map(flag => {
                  const total = flag.active + flag.resolved
                  const rate = total > 0 ? (flag.resolved / total) * 100 : 0
                  return (
                    <div key={flag.name}>
                      <div className="flex justify-between text-xs mb-1"><span>{flag.name}</span><span className="font-medium">{rate.toFixed(0)}%</span></div>
                      <Progress value={rate} className="h-2" />
                      <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5"><span>{flag.active} active</span><span>{flag.resolved} resolved</span></div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}
        {escalationRates.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Escalated Conversations by Category <InfoTooltip text="Percentage of escalated conversations that include each category." /></CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_50px_60px] gap-2 text-[10px] font-medium text-muted-foreground uppercase px-1">
                  <span>Category</span><span className="text-right">Count</span><span className="text-right">% escalated</span>
                </div>
                {escalationRates.map(rate => (
                  <div key={rate.name} className="grid grid-cols-[1fr_50px_60px] gap-2 text-xs items-center px-1 py-1 rounded hover:bg-muted/50">
                    <span className="truncate">{rate.name}</span>
                    <span className="text-right font-medium">{rate.count}</span>
                    <span className="text-right text-muted-foreground">{rate.percentage.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Flag Drill-down Dialog */}
      <Dialog open={!!selectedBreakdown} onOpenChange={open => { if (!open) setSelectedBreakdown(null) }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              {selectedBreakdown?.name}
              <Badge variant="outline" className="text-[10px] font-normal">{selectedBreakdown?.contacts?.length || 0} contacts</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-1">
            {(["all", "active", "resolved"] as const).map(f => (
              <Button key={f} variant={dialogFilter === f ? "default" : "outline"} size="sm" className="h-7 text-xs" onClick={() => setDialogFilter(f)}>
                {f === "all" ? `All (${selectedBreakdown?.contacts?.length || 0})` :
                  f === "active" ? `Active (${selectedBreakdown?.contacts?.filter(c => c.status === "active").length || 0})` :
                    `Resolved (${selectedBreakdown?.contacts?.filter(c => c.status === "resolved").length || 0})`}
              </Button>
            ))}
          </div>
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-1">
              {dialogContacts.map((contact, i) => (
                <div key={`${contact.contactId}-${i}`} className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-muted/50 group">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant={contact.status === "active" ? "destructive" : "secondary"} className="text-[9px] px-1.5 py-0 shrink-0">{contact.status}</Badge>
                    <div className="min-w-0">
                      <div className="text-xs font-medium flex items-center gap-1">
                        <Phone className="h-3 w-3 shrink-0 text-muted-foreground" />
                        <span className="truncate">{contact.phoneNumber || contact.contactId}</span>
                      </div>
                      {contact.name && (
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <User className="h-2.5 w-2.5 shrink-0" /><span className="truncate">{contact.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" onClick={() => window.open(`/moderation?contact=${contact.contactId}`, "_blank")}>
                    <ExternalLink className="h-3 w-3 mr-1" />Moderation
                  </Button>
                </div>
              ))}
              {dialogContacts.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No contacts match this filter</p>}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Generic Contact List Dialog */}
      <ContactListDialog
        open={!!contactDialog}
        onOpenChange={open => { if (!open) setContactDialog(null) }}
        title={contactDialog?.title || ""}
        contacts={contactDialog?.contacts || []}
        showStatusBadge={contactDialog?.showStatusBadge}
      />
    </div>
  )
}
